// EditSheet — modal de edición genérico tipo "bottom sheet".
// Props: open, title, onClose, onSave, children.
import { useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Check } from "lucide-react";

export default function EditSheet({ open, title, onClose, onSave, onDelete, children, saving = false }) {
  // Lock scroll when open
  useEffect(() => {
    if (open) {
      document.body.style.overflow = "hidden";
      return () => { document.body.style.overflow = ""; };
    }
  }, [open]);

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[1100] bg-black/40 flex items-end justify-center"
          onClick={onClose}
          data-testid="edit-sheet-backdrop"
        >
          <motion.div
            initial={{ y: "100%" }}
            animate={{ y: 0 }}
            exit={{ y: "100%" }}
            transition={{ type: "spring", stiffness: 320, damping: 30 }}
            onClick={(e) => e.stopPropagation()}
            className="w-full max-w-md bg-[hsl(var(--bg))] rounded-t-3xl shadow-2xl max-h-[92vh] overflow-y-auto"
            style={{ paddingBottom: "calc(env(safe-area-inset-bottom, 0px) + 16px)" }}
            data-testid="edit-sheet"
          >
            <div className="sticky top-0 bg-[hsl(var(--bg))] z-10 flex items-center justify-between px-5 py-4 border-b border-[hsl(var(--line))]">
              <button onClick={onClose} className="text-[hsl(var(--muted))] active:scale-95" data-testid="edit-sheet-cancel">
                <X size={20} />
              </button>
              <h2 className="text-sm font-bold">{title}</h2>
              <button onClick={onSave} disabled={saving} className="text-[hsl(var(--accent))] font-semibold active:scale-95 disabled:opacity-40" data-testid="edit-sheet-save">
                {saving ? "…" : <Check size={20} />}
              </button>
            </div>
            <div className="px-5 py-4">{children}</div>
            {onDelete && (
              <div className="px-5 pb-4">
                <button
                  onClick={onDelete}
                  className="w-full py-2.5 rounded-xl text-sm font-medium text-red-600 bg-red-50 active:scale-[0.98]"
                  data-testid="edit-sheet-delete"
                >
                  Eliminar
                </button>
              </div>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

// Re-usable form field
export function Field({ label, children }) {
  return (
    <label className="block mb-3">
      <span className="block text-[10px] uppercase tracking-wider font-semibold text-[hsl(var(--muted))] mb-1">{label}</span>
      {children}
    </label>
  );
}

export const inputCls = "w-full p-2.5 rounded-xl bg-white border border-[hsl(var(--line))] text-sm focus:border-[hsl(var(--accent))] focus:outline-none";
